import { callRpc } from "@/lib/api/rpc";

export type DocType = "purchase_order" | "stock_transfer";

export interface BarcodeMatch {
  kind: "product" | "variant";
  id: string;
  product_id: string;
  variant_id: string | null;
  name: string;
  sku: string | null;
  barcode: string | null;
}

export interface BarcodeResolution {
  found: boolean;
  ambiguous: boolean;
  matches: BarcodeMatch[];
}

export interface ReceiveScanResult {
  ok: boolean;
  product_name?: string;
  sku?: string;
  scanned_qty?: number;
  remaining?: number;
}

/** Deterministically resolve a scanned value to a product/variant within the org. */
export async function resolveBarcode(orgId: string, value: string): Promise<BarcodeResolution> {
  const data = await callRpc<BarcodeResolution>("resolve_barcode", { p_org_id: orgId, p_value: value });
  return data ?? { found: false, ambiguous: false, matches: [] };
}

/** Increment the scanned quantity for a document line (PO or transfer). */
export async function recordReceiveScan(docType: DocType, docId: string, barcode: string): Promise<ReceiveScanResult> {
  const data = await callRpc<ReceiveScanResult>("record_receive_scan", {
    p_doc_type: docType,
    p_doc_id: docId,
    p_barcode: barcode,
  });
  return data ?? { ok: false };
}

/** Commit all scanned quantities through the trusted receiving operation. */
export async function commitReceiveScan(docType: DocType, docId: string): Promise<{ ok: boolean; lines?: number }> {
  const data = await callRpc<{ ok: boolean; lines?: number }>("commit_receive_scan", { p_doc_type: docType, p_doc_id: docId });
  return data ?? { ok: false };
}

/** Clear a pending scan session (whole session, or a single line). */
export async function resetReceiveScan(docType: DocType, docId: string, lineId?: string): Promise<void> {
  await callRpc("reset_receive_scan", {
    p_doc_type: docType,
    p_doc_id: docId,
    p_line_id: lineId ?? null,
  });
}